package world.phantasmal.web.questEditor.controllers

import kotlinx.coroutines.await
import mu.KotlinLogging
import world.phantasmal.cell.*
import world.phantasmal.core.*
import world.phantasmal.core.externals.browser.FileSystemDirectoryHandle
import world.phantasmal.core.externals.browser.arrayBuffer
import world.phantasmal.psolib.Endianness
import world.phantasmal.psolib.Episode
import world.phantasmal.psolib.asm.dataFlowAnalysis.FloorMapping
import world.phantasmal.psolib.buffer.Buffer
import world.phantasmal.psolib.compression.prs.prsCompress
import world.phantasmal.psolib.cursor.cursor
import world.phantasmal.psolib.fileFormats.quest.*
import world.phantasmal.web.questEditor.loading.FreeRoamAreaInfo
import world.phantasmal.web.questEditor.loading.LOBBY_FLOOR_ID
import world.phantasmal.web.questEditor.loading.extractRawEntityDataByFloor
import world.phantasmal.web.questEditor.loading.getLobbyVariant
import world.phantasmal.web.questEditor.loading.parseFreeRoamFilename
import world.phantasmal.web.questEditor.loading.parseLobbyDatFilename
import world.phantasmal.cell.list.ListCell
import world.phantasmal.cell.list.mutableListCell
import world.phantasmal.web.core.PwToolType
import world.phantasmal.web.core.files.cursor
import world.phantasmal.web.core.files.writeBuffer
import world.phantasmal.web.core.stores.UiStore
import world.phantasmal.web.questEditor.models.AreaModel
import world.phantasmal.web.questEditor.models.AreaVariantModel
import world.phantasmal.web.questEditor.models.LobbyEventFilter
import world.phantasmal.web.questEditor.models.QuestModel
import world.phantasmal.web.questEditor.models.SectionModel
import world.phantasmal.web.questEditor.rendering.QuestMapExporter
import world.phantasmal.web.questEditor.stores.AsmStore
import world.phantasmal.web.questEditor.stores.AreaStore
import world.phantasmal.web.questEditor.stores.QuestSaveCoverage
import world.phantasmal.web.questEditor.stores.QuestEditorStore
import world.phantasmal.web.questEditor.stores.QuestEditorUiStore
import world.phantasmal.web.questEditor.stores.WalkthroughPlayer
import world.phantasmal.web.questEditor.stores.convertQuestFromModel
import world.phantasmal.web.questEditor.stores.convertQuestToModel
import world.phantasmal.webui.UserAgentFeatures
import world.phantasmal.webui.controllers.Controller
import world.phantasmal.webui.files.*
import world.phantasmal.webui.files.showDirectoryPicker
import world.phantasmal.webui.obj

private val logger = KotlinLogging.logger {}

enum class SaveFormat {
    QST,
    BIN_DAT,
    LOBBY_DAT,
    FREE_ROAM,
}

class AreaAndLabel(
    val area: AreaModel,
    val label: String,
    val variant: AreaVariantModel? = null,
    /** Floor IDs associated with this entry, used to filter entities. */
    val floorIds: Set<Int>? = null,
)

class QuestEditorToolbarController(
    uiStore: UiStore,
    private val areaStore: AreaStore,
    private val questEditorStore: QuestEditorStore,
    private val questEditorUiStore: QuestEditorUiStore,
    private val asmStore: AsmStore,
    private val mapExporter: QuestMapExporter? = null,
) : Controller() {
    private val _resultDialogVisible = mutableCell(false)
    private val _result = mutableCell<PwResult<*>?>(null)
    private val saving = mutableCell(false)
    private val _selectedAreaAndLabel = mutableCell<AreaAndLabel?>(null)
    /** True while [setCurrentArea] is updating the store, to avoid clearing the selection. */
    private var settingAreaFromDropdown = false
    private var gameDirHandle: FileSystemDirectoryHandle? = null

    // City map toggle (View menu)
    private val _showCityMap = mutableCell(false)
    val showCityMap: Cell<Boolean> = _showCityMap

    // Lobby menu visibility (hidden by default, toggled via Ctrl+L)
    private val _showLobbyMenu = mutableCell(false)
    val showLobbyMenu: Cell<Boolean> = _showLobbyMenu

    val walkthroughPlayers: Cell<List<WalkthroughPlayer>> = cell(WalkthroughPlayer.entries)
    val walkthroughPlayer: Cell<WalkthroughPlayer> = questEditorUiStore.walkthroughPlayer

    // Free roam variant state (delegated to FreeRoamController)
    val freeRoam = FreeRoamController()

    // We mainly disable saving while a save is underway for visual feedback that a save is
    // happening/has happened.
    private val savingEnabled = questEditorStore.currentQuest.isNotNull() and !saving
    private val _saveAsDialogVisible = mutableCell(false)
    private val fileHolder = mutableCell<FileHolder?>(null)
    private var saveCoverage: Pair<SaveFormat, QuestSaveCoverage>? = null
    private val _filename = mutableCell("")
    private val _version = mutableCell(Version.BB_V4)
    private val _compressed = mutableCell(true)
    private val _saveFormat = mutableCell(SaveFormat.QST)

    val saveFormat: Cell<SaveFormat> = _saveFormat
    val compressedVisible: Cell<Boolean> = _saveFormat.map { it == SaveFormat.BIN_DAT }
    val freeRoamFormatAvailable: Boolean = UserAgentFeatures.directoryPickerApi
    private val standardSaveFormats: List<SaveFormat> = buildList {
        add(SaveFormat.QST)
        add(SaveFormat.BIN_DAT)
        if (UserAgentFeatures.directoryPickerApi) add(SaveFormat.FREE_ROAM)
    }
    private val _availableSaveFormats = mutableListCell(*standardSaveFormats.toTypedArray())
    val availableSaveFormats: ListCell<SaveFormat> = _availableSaveFormats

    // Result

    val resultDialogVisible: Cell<Boolean> = _resultDialogVisible
    val result: Cell<PwResult<*>?> = _result

    val supportedFileTypes = listOf(
        FileType(
            description = "Quests and Free Roam Files",
            accept = mapOf("application/pw-quest" to setOf(".qst", ".bin", ".dat")),
        ),
    )

    // Saving

    val saveEnabled: Cell<Boolean> =
        savingEnabled and questEditorStore.canSaveChanges and UserAgentFeatures.fileSystemApi
    val saveTooltip: Cell<String> =
        if (UserAgentFeatures.fileSystemApi) {
            questEditorStore.canSaveChanges.map {
                (if (it) "Save changes" else "No changes to save") + " (Ctrl-S)"
            }
        } else {
            cell("This browser doesn't support saving changes to existing files")
        }
    val saveAsEnabled: Cell<Boolean> = savingEnabled
    val saveAsDialogVisible: Cell<Boolean> = _saveAsDialogVisible
    val showSaveAsDialogNameField: Boolean = !UserAgentFeatures.fileSystemApi
    val filename: Cell<String> = _filename
    val version: Cell<Version> = _version
    val compressed: Cell<Boolean> = _compressed

    // Undo

    val undoTooltip: Cell<String> = questEditorStore.firstUndo.map { command ->
        (command?.let { "Undo \"${command.description}\"" } ?: "Nothing to undo") + " (Ctrl-Z)"
    }

    val undoEnabled: Cell<Boolean> = questEditorStore.canUndo

    // Redo

    val redoTooltip: Cell<String> = questEditorStore.firstRedo.map { command ->
        (command?.let { "Redo \"${command.description}\"" } ?: "Nothing to redo") +
                " (Ctrl-Shift-Z)"
    }

    val redoEnabled: Cell<Boolean> = questEditorStore.canRedo

    // Map export

    private val _mapExporting = mutableCell(false)
    val mapExporting: Cell<Boolean> = _mapExporting
    val exportMapsEnabled: Cell<Boolean> =
        if (mapExporter == null) cell(false)
        else questEditorStore.currentQuest.isNotNull() and !_mapExporting

    suspend fun exportMaps() {
        val exporter = mapExporter ?: return
        val quest = questEditorStore.currentQuest.value ?: return
        if (_mapExporting.value) return

        try {
            _mapExporting.value = true
            exporter.exportQuest(quest, areas.value, questEditorUiStore.ultimate.value)
        } catch (e: Throwable) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(
                        Severity.Error,
                        "Couldn't export maps: ${e.message ?: e::class.simpleName ?: "unknown error"}",
                        cause = e,
                    )
                    .failure()
            )
        } finally {
            _mapExporting.value = false
        }
    }

    // Areas

    /**
     * Builds the area dropdown list for the toolbar. Reactively updates when entities are
     * added/removed (entity counts in labels) or when the current quest changes.
     *
     * Both branches maintain logical floor order (Pioneer II → Forest 1 → … etc. for defaults).
     *
     * Two branches depending on whether the quest has resolved floor mappings:
     *
     * **With floorMappings** (challenge mode, multi-floor quests like PW4):
     *   - One floor can map to an area+variant (e.g., floor 16 → Tower variant 1).
     *   - Each logical floor has its own entry prefixed with its actual floor ID.
     *   - Unmapped episode areas are interleaved in canonical order (not appended at the end)
     *     without variant suffix or entity count. All floors are always shown — do NOT filter
     *     out empty areas.
     *
     * **Without floorMappings** (regular quests):
     *   - All areas for the episode are listed. Areas with multiple variants expand into
     *     separate entries (e.g., "Cave 1 - Map 1", "Cave 1 - Map 2").
     */
    val areas: Cell<List<AreaAndLabel>> = questEditorStore.currentQuest.flatMap { quest ->
        quest?.let {
            map(quest.entitiesPerArea, quest.areaVariants) { entitiesPerArea, variants ->
                val result = mutableListOf<AreaAndLabel>()

                if (quest.floorMappings.isNotEmpty()) {
                    // The client's designation table is indexed by logical floor, not map area.
                    // Place each effective map in its actual floor slot; mapAreaId is not globally
                    // unique and cannot be used to infer floor identity.
                    val mappingsByFloorId = quest.floorMappings.associateBy(FloorMapping::floorId)

                    val addedFloorIds = mutableSetOf<Int>()

                    // Episode-default area IDs match their logical floor slots.
                    for (episodeArea in areaStore.getAreasForEpisode(quest.episode)) {
                        val mapping = mappingsByFloorId[episodeArea.id]

                        if (mapping != null) {
                            addMappedArea(result, quest, mapping)
                            addedFloorIds.add(mapping.floorId)
                        } else {
                            // Unmapped area: show without variant suffix or entity count.
                            val displayName = buildAreaDisplayName(episodeArea, null, null)
                            result.add(AreaAndLabel(episodeArea, displayName))
                        }
                    }

                    // EP4 has fewer named default areas than the client's 18 floor slots. Preserve
                    // any explicitly designated higher floor after the canonical slots.
                    for (mapping in quest.floorMappings.sortedBy(FloorMapping::floorId)) {
                        if (mapping.floorId !in addedFloorIds) {
                            addMappedArea(
                                result,
                                quest,
                                mapping,
                            )
                        }
                    }
                } else {
                    for (area in areaStore.getAreasForEpisode(quest.episode)) {
                        val entityCount = getEntityCountForArea(entitiesPerArea, area)
                        val areaVariants = getAreaVariants(area, variants)

                        if (areaVariants.isNotEmpty()) {
                            for (variant in areaVariants) {
                                val displayName = buildAreaDisplayName(area, variant, entityCount)
                                result.add(AreaAndLabel(area, displayName, variant))
                            }
                        } else {
                            val displayName = buildAreaDisplayName(area, null, entityCount)
                            result.add(AreaAndLabel(area, displayName))
                        }
                    }
                }

                result
            }
        } ?: cell(emptyList())
    }

    private fun getEntityCountForArea(entitiesPerArea: Map<Int, Int>, area: AreaModel): Int? {
        return entitiesPerArea[area.id]
    }

    private fun addMappedArea(
        result: MutableList<AreaAndLabel>,
        quest: QuestModel,
        mapping: FloorMapping,
    ) {
        val mapEpisode = mapping.mapEpisode ?: quest.episode
        val area = areaStore.getArea(mapEpisode, mapping.mapAreaId) ?: return
        val variant =
            areaStore.getVariant(mapEpisode, mapping.mapAreaId, mapping.mapVariation) ?: return
        val floorId = mapping.floorId
        val entityCount =
            quest.npcs.value.count { it.floorId == floorId } +
                quest.objects.value.count { it.floorId == floorId }
        val ambiguitySuffix = if (mapping.runtimeAmbiguous) " [runtime-dependent]" else ""
        val displayName = "Floor $floorId — " +
            buildAreaDisplayName(area, variant, entityCount.takeIf { it > 0 }) +
            ambiguitySuffix

        result.add(AreaAndLabel(area, displayName, variant, setOf(floorId)))
    }

    private fun getAreaVariants(area: AreaModel, variants: List<AreaVariantModel>): List<AreaVariantModel> {
        return variants.filter { it.area.id == area.id }.sortedBy { it.id }
    }

    private fun buildAreaDisplayName(area: AreaModel, variant: AreaVariantModel?, entityCount: Int?): String {
        val baseName = variant?.name ?: area.name
        val mapSuffix = getMapVariantSuffix(area, variant?.id)
        val countSuffix = createCountSuffix(entityCount)

        return baseName + mapSuffix + countSuffix
    }

    private fun getMapVariantSuffix(area: AreaModel, variantId: Int?): String {
        return when {
            area.id <= 0 -> ""
            area.bossArea -> ""
            area.name == "Lobby" && variantId != null ->
                " ${variantId.toString().padStart(2, '0')}"
            variantId != null -> " - Map ${variantId + 1}"  // Show Map X if variant is specified
            else -> ""  // No Map suffix for areas without variants
        }
    }

    private fun createCountSuffix(entityCount: Int?): String {
        return entityCount?.let { " ($it)" } ?: ""
    }

    val currentArea: Cell<AreaAndLabel?> = areas.flatMap { areas ->
        map(_selectedAreaAndLabel, questEditorStore.currentArea, questEditorStore.currentFloorIds) { selectedAreaAndLabel, storeCurrentArea, storeFloorIds ->
            // Resolve an explicit selection against the latest list. Labels and mappings are
            // rebuilt reactively, so retaining the old object would leave the dropdown stale.
            selectedAreaAndLabel?.let { selected ->
                areas.find { candidate ->
                    candidate.floorIds == selected.floorIds &&
                        candidate.area.id == selected.area.id &&
                        candidate.variant?.episode == selected.variant?.episode &&
                        candidate.variant?.id == selected.variant?.id
                }
            } ?:
            // Match by floorIds first (most precise), then by area, then fallback
            if (storeFloorIds != null) {
                areas.find { it.floorIds == storeFloorIds }
            } else if (storeCurrentArea != null) {
                areas.find { it.area.id == storeCurrentArea.id }
            } else null ?:
            // Final fallback to first area
            areas.firstOrNull()
        }
    }

    val areaSelectEnabled: Cell<Boolean> = questEditorStore.currentQuest.isNotNull()

    // Settings

    val showCollisionGeometry: Cell<Boolean> = questEditorUiStore.showCollisionGeometry
    val showSectionIds: Cell<Boolean> = questEditorUiStore.showSectionIds
    val showDoorIds: Cell<Boolean> = questEditorUiStore.showDoorIds
    val showEntityDirections: Cell<Boolean> = questEditorUiStore.showEntityDirections
    val spawnMonstersOnGround: Cell<Boolean> = questEditorUiStore.spawnMonstersOnGround
    val showOriginPoint: Cell<Boolean> = questEditorUiStore.showOriginPoint
    val showQuestParticles: Cell<Boolean> = questEditorUiStore.showQuestParticles
    val showFogBoundaries: Cell<Boolean> = questEditorUiStore.showFogBoundaries
    val ultimate: Cell<Boolean> = questEditorUiStore.ultimate

    // Lobby event (Pioneer 2 / city seasonal decoration) filter.
    //
    // Dynamic options: "None" is always first; then one entry per lobby event that actually has
    // decoration objects in the current area; then "All" if at least one event is present.
    val lobbyEventOptions: Cell<List<LobbyEventFilter>> =
        questEditorStore.currentQuest.flatMap { quest ->
            if (quest == null) {
                cell(listOf<LobbyEventFilter>(LobbyEventFilter.None))
            } else {
                map(
                    quest.objects,
                    questEditorStore.currentArea,
                    questEditorStore.currentFloorIds,
                ) { objects, area, floorIds ->
                    if (area == null) {
                        listOf(LobbyEventFilter.None)
                    } else {
                        val present = objects
                            .filter {
                                if (floorIds != null) it.floorId in floorIds else it.floorId == area.id
                            }
                            .mapNotNull { it.type.lobbyEvent }
                            .distinct()
                            .sortedBy { it.ordinal }
                        buildList {
                            add(LobbyEventFilter.None)
                            present.forEach { add(LobbyEventFilter.Event(it)) }
                            if (present.isNotEmpty()) add(LobbyEventFilter.All)
                        }
                    }
                }
            }
        }

    // Only show the dropdown when there is at least one event to choose (size > 1 means more
    // than just "None").
    val showLobbyEventSelect: Cell<Boolean> = lobbyEventOptions.map { it.size > 1 }

    // Cell<out T> is covariant, so a Cell<LobbyEventFilter> is usable as Cell<LobbyEventFilter?>.
    val selectedLobbyEvent: Cell<LobbyEventFilter> = questEditorUiStore.selectedLobbyEvent

    fun setSelectedLobbyEvent(filter: LobbyEventFilter) {
        questEditorUiStore.setSelectedLobbyEvent(filter)
    }

    // Free roam variant controls (delegated)

    val freeRoamV1Options: ListCell<Int> get() = freeRoam.freeRoamV1Options
    val freeRoamV2Options: ListCell<Int> get() = freeRoam.freeRoamV2Options
    val showFreeRoamV1: Cell<Boolean> get() = freeRoam.showFreeRoamV1
    val showFreeRoamV2: Cell<Boolean> get() = freeRoam.showFreeRoamV2
    val showFreeRoamOnOff: Cell<Boolean> get() = freeRoam.showFreeRoamOnOff
    val showFreeRoamUltimate: Cell<Boolean> get() = freeRoam.showFreeRoamUltimate
    val freeRoamV1: Cell<Int?> get() = freeRoam.freeRoamV1
    val freeRoamV2: Cell<Int?> get() = freeRoam.freeRoamV2
    val freeRoamOffline: Cell<Boolean> get() = freeRoam.freeRoamOffline
    val freeRoamUltimate: Cell<Boolean> get() = freeRoam.freeRoamUltimate

    // Go to Section functionality
    // Use the store's selectedSection directly to ensure sync when clicking events
    val selectedSection: Cell<SectionModel?> = questEditorStore.selectedSection
    val gotoSectionEnabled: Cell<Boolean> = questEditorStore.currentAreaVariant.isNotNull()

    // Go to World Position. Three small numeric inputs (X/Y/Z); each commits to the goto on
    // change. Pasting a tuple (e.g. "(-10230, -2, -10)") into any field smart-distributes to
    // all three — see [smartPasteGotoPosition].
    val gotoWorldPositionEnabled: Cell<Boolean> = questEditorStore.currentQuest.isNotNull()
    private val _gotoWorldX = mutableCell(0.0)
    private val _gotoWorldY = mutableCell(0.0)
    private val _gotoWorldZ = mutableCell(0.0)
    val gotoWorldX: Cell<Double> = _gotoWorldX
    val gotoWorldY: Cell<Double> = _gotoWorldY
    val gotoWorldZ: Cell<Double> = _gotoWorldZ

    private val gotoSectionFilter: MutableCell<(SectionModel) -> Boolean> =
        mutableCell { true }

    val filteredSections: Cell<List<SectionModel>> =
        map(questEditorStore.currentAreaSections, gotoSectionFilter) { sections, filter ->
            sections.filter(filter)
        }

    init {
        // Clear explicit area selection when the store's area changes externally (e.g. via
        // goToEvent or setSelectedEntity), so the toolbar shows the correct current area.
        observe(questEditorStore.currentArea) {
            if (!settingAreaFromDropdown) {
                _selectedAreaAndLabel.value = null
            }
        }

        // When the area (and thus its available events) changes, drop a selection that is no
        // longer valid so a stale event doesn't silently filter the new area.
        observeNow(lobbyEventOptions) { options ->
            if (questEditorUiStore.selectedLobbyEvent.value !in options) {
                questEditorUiStore.setSelectedLobbyEvent(LobbyEventFilter.None)
            }
        }

        addDisposables(
            freeRoam,

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-O") {
                openFiles(showOpenFilePicker(supportedFileTypes, multiple = true))
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-S") {
                save()
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-Shift-S") {
                saveAs()
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-Z") {
                undo()
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-Shift-Z") {
                redo()
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-Y") {
                redo()
            },

            uiStore.onGlobalKeyDown(PwToolType.QuestEditor, "Ctrl-L") {
                _showLobbyMenu.value = !_showLobbyMenu.value
            },
        )
    }

    /** Episode of the currently loaded quest, defaults to Episode I. */
    val currentEpisode: Episode
        get() = questEditorStore.currentQuest.value?.episode ?: Episode.I

    suspend fun createNewQuest(episode: Episode) {
        freeRoam.clearFreeRoamState()
        val quest = if (_showCityMap.value) {
            questEditorStore.getCityQuest(episode)
        } else {
            questEditorStore.getDefaultQuest(episode)
        }
        setCurrentQuest(fileHolder = null, Version.BB_V4, quest)
    }

    suspend fun loadCityQuest(episode: Episode) {
        freeRoam.clearFreeRoamState()
        _showCityMap.value = true
        setCurrentQuest(fileHolder = null, Version.BB_V4, questEditorStore.getCityQuest(episode))
    }

    suspend fun loadLobbyQuest(variant: Int) {
        freeRoam.clearFreeRoamState()
        _showCityMap.value = false
        val definition = requireNotNull(getLobbyVariant(variant)) {
            "Unknown lobby number $variant."
        }
        setCurrentQuest(
            FileHolder.LobbyDat(null, definition.datFileName),
            Version.BB_V4,
            questEditorStore.getLobbyQuest(variant),
        )
    }

    suspend fun setShowCityMap(show: Boolean) {
        if (show) {
            loadCityQuest(currentEpisode)
        } else {
            _showCityMap.value = false
            createNewQuest(currentEpisode)
        }
    }

    suspend fun openFiles(newFiles: List<FileHandle>?) {
        try {
            if (newFiles.isNullOrEmpty()) return

            when (val strategy = resolveLoadStrategy(newFiles)) {
                is QuestLoadStrategy.Qst -> loadQst(strategy)
                is QuestLoadStrategy.BinDat -> loadBinDat(strategy)
                is QuestLoadStrategy.LobbyDat -> loadLobbyDat(strategy)
                is QuestLoadStrategy.FreeRoam -> loadFreeRoam(strategy)
                null -> setResult(
                    Failure(
                        listOf(
                            Problem(
                                Severity.Error,
                                "Please select a .qst file, a .bin file, a .bin + .dat pair, or a free roam .dat file.",
                            )
                        )
                    )
                )
            }
        } catch (e: Throwable) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(Severity.Error, "Couldn't parse file.", cause = e)
                    .failure()
            )
        }
    }

    private fun resolveLoadStrategy(files: List<FileHandle>): QuestLoadStrategy? {
        val qstFile = files.find { it.extension().equals("qst", ignoreCase = true) }
        if (qstFile != null) return QuestLoadStrategy.Qst(qstFile)

        val binFile = files.find { it.extension().equals("bin", ignoreCase = true) }
        val datFile = files.find { it.extension().equals("dat", ignoreCase = true) }

        // Regular bin+dat pair takes priority over free roam detection.
        if (binFile != null && datFile != null) {
            return QuestLoadStrategy.BinDat(binFile, datFile)
        }

        // Check if a single file matches a free roam pattern (bin or dat).
        val freeRoamFile = binFile ?: datFile
        if (freeRoamFile != null) {
            if (datFile != null && binFile == null) {
                val lobbyVariant = parseLobbyDatFilename(datFile.name)
                if (lobbyVariant != null) {
                    return QuestLoadStrategy.LobbyDat(datFile, lobbyVariant)
                }
            }

            val freeRoamInfo = parseFreeRoamFilename(freeRoamFile.name)
            if (freeRoamInfo != null) return QuestLoadStrategy.FreeRoam(freeRoamInfo)
        }

        return null
    }

    private suspend fun loadQst(strategy: QuestLoadStrategy.Qst) {
        freeRoam.clearFreeRoamState()
        val parseResult = parseQstToQuest(strategy.file.cursor(Endianness.Little))
        setResult(parseResult)

        if (parseResult is Success) {
            setCurrentQuest(
                FileHolder.Qst(strategy.file),
                parseResult.value.version,
                parseResult.value.quest,
            )
        }
    }

    private suspend fun loadBinDat(strategy: QuestLoadStrategy.BinDat) {
        freeRoam.clearFreeRoamState()
        val shiftJis = isJapaneseFilename(strategy.binFile.name)
        val parseResult = parseBinDatToQuestAutoDetect(
            strategy.binFile.cursor(Endianness.Little),
            strategy.datFile.cursor(Endianness.Little),
            shiftJis = shiftJis,
        )
        setResult(parseResult)

        if (parseResult is Success) {
            setCurrentQuest(
                FileHolder.BinDat(strategy.binFile, strategy.datFile, parseResult.value.compressed),
                parseResult.value.quest.version,
                parseResult.value.quest,
            )
        }
    }

    private suspend fun loadLobbyDat(strategy: QuestLoadStrategy.LobbyDat) {
        freeRoam.clearFreeRoamState()
        _showCityMap.value = false
        val objectData = strategy.file.cursor(Endianness.Little).buffer()
        setCurrentQuest(
            FileHolder.LobbyDat(strategy.file, strategy.file.name),
            Version.BB_V4,
            questEditorStore.getLobbyQuest(strategy.variant, objectData),
        )
    }

    private suspend fun loadFreeRoam(strategy: QuestLoadStrategy.FreeRoam) {
        if (!UserAgentFeatures.directoryPickerApi) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(
                        Severity.Error,
                        "Loading free roam maps requires a browser that supports the File System Access API (Chrome or Edge).",
                    )
                    .failure()
            )
            return
        }

        var dirHandle = gameDirHandle

        if (dirHandle == null) {
            dirHandle = showDirectoryPicker() ?: return
            gameDirHandle = dirHandle
        }

        val info = strategy.info
        val result = questEditorStore.getFreeRoamQuest(dirHandle, info)

        freeRoam.setupFreeRoamState(
            episode = info.episode,
            floorRange = info.floorRange,
            gameDirHandle = dirHandle,
            binPrefix = info.binPrefix,
            isCity = info.isCity,
            initialOffline = info.offline,
            initialUltimate = info.ultimate,
            tokenBase = info.tokenBase,
        )

        setCurrentQuest(
            FileHolder.FreeRoamDir(dirHandle, result.binName, result.datFilesByFloor),
            Version.BB_V4,
            result.questModel,
        )
    }

    /** Captures the exact revision serialized before any file picker or write can suspend. */
    private fun prepareSave(quest: QuestModel, format: SaveFormat): ((FileHolder?) -> Unit)? {
        val result = asmStore.commit()
        if (result is Failure) {
            setResult(result)
            return null
        }
        if (format == SaveFormat.LOBBY_DAT || format == SaveFormat.FREE_ROAM) {
            val coverage = saveCoverage
            if (coverage == null || coverage.first != format || !coverage.second.canSave(quest)) {
                setResult(PwResult.build<Nothing>(logger)
                    .addProblem(Severity.Error,
                        "This format cannot store all current changes. Undo changes outside the file's supported contents before saving.")
                    .failure())
                return null
            }
        }
        val markSaved = questEditorStore.captureSavePoint()
        return { holder ->
            if (!disposed && questEditorStore.currentQuest.value === quest) {
                if (holder != null) setFileHolder(holder)
                markSaved()
            }
        }
    }

    suspend fun save() {
        if (!saveEnabled.value) return

        try {
            saving.value = true

            val quest = questEditorStore.currentQuest.value ?: return
            val format = when (fileHolder.value) {
                is FileHolder.LobbyDat -> SaveFormat.LOBBY_DAT
                is FileHolder.FreeRoamDir -> SaveFormat.FREE_ROAM
                else -> SaveFormat.QST
            }
            val saved = prepareSave(quest, format) ?: return
            val headerFilename = filename.value.trim()

            when (val holder = fileHolder.value) {
                is FileHolder.Qst -> {
                    if (holder.file is FileHandle.System) {
                        val buffer = writeQuestToQst(
                            convertQuestFromModel(quest),
                            headerFilename,
                            version.value,
                            online = true,
                            compressed = compressed.value,
                        )

                        holder.file.writeBuffer(buffer)

                        saved(null)
                        return
                    }
                }

                is FileHolder.BinDat -> {
                    if (holder.binFile is FileHandle.System &&
                        holder.datFile is FileHandle.System
                    ) {
                        var (bin, dat) = writeQuestToBinDat(
                            convertQuestFromModel(quest),
                            version.value,
                        )

                        if (compressed.value) {
                            bin = prsCompress(bin.cursor()).buffer()
                            dat = prsCompress(dat.cursor()).buffer()
                        }

                        holder.binFile.writeBuffer(bin)
                        holder.datFile.writeBuffer(dat)

                        saved(null)
                        return
                    }
                }

                is FileHolder.FreeRoamDir -> {
                    saveToFreeRoamDir(holder, quest, saved)
                    return
                }

                is FileHolder.LobbyDat -> {
                    val file = holder.file
                    if (file is FileHandle.System) {
                        file.writeBuffer(extractLobbyObjectData(quest))
                        saved(null)
                        return
                    }
                }

                else -> {}
            }

            // When there's no existing file that can be saved, default to "Save as...".
            _saveAsDialogVisible.value = true
        } catch (e: Throwable) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(Severity.Error, "Couldn't save file.", cause = e)
                    .failure()
            )
        } finally {
            saving.value = false
        }
    }

    fun saveAs() {
        if (saveAsEnabled.value) {
            _saveAsDialogVisible.value = true
        }
    }

    fun setFilename(filename: String) {
        _filename.value = filename
    }

    fun setVersion(version: Version) {
        _version.value = version
    }

    fun setCompressed(compressed: Boolean) {
        _compressed.value = compressed
    }

    fun setSaveFormat(format: SaveFormat) {
        _saveFormat.value = format
    }

    suspend fun saveAsDialogSave() {
        if (!saveAsEnabled.value) return

        val quest = questEditorStore.currentQuest.value ?: return

        try {
            saving.value = true
            val saved = prepareSave(quest, _saveFormat.value) ?: return

            when (_saveFormat.value) {
                SaveFormat.QST -> saveAsQst(quest, saved)
                SaveFormat.BIN_DAT -> saveAsBinDat(quest, saved)
                SaveFormat.LOBBY_DAT -> saveAsLobbyDat(quest, saved)
                SaveFormat.FREE_ROAM -> saveAsFreeRoam(quest, saved)
            }
        } catch (e: Throwable) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(Severity.Error, "Couldn't save file.", cause = e)
                    .failure()
            )
        } finally {
            dismissSaveAsDialog()
            saving.value = false
        }
    }

    private suspend fun saveAsQst(quest: QuestModel, saved: (FileHolder?) -> Unit) {
        val headerFilename = filename.value.trim()
        val filename =
            if (headerFilename.endsWith(".qst")) headerFilename
            else "$headerFilename.qst"

        val buffer = writeQuestToQst(
            convertQuestFromModel(quest),
            headerFilename,
            version.value,
            online = true,
            compressed = true, // QST is always PRS compressed.
        )

        if (UserAgentFeatures.fileSystemApi) {
            val fileHandle = showSaveFilePicker(
                listOf(
                    FileType("Quest file", mapOf("application/pw-quest" to setOf(".qst")))
                )
            )

            if (fileHandle != null) {
                fileHandle.writableStream().use { it.write(buffer.arrayBuffer).await() }

                saved(FileHolder.Qst(fileHandle))
            }
        } else {
            val fileHandle = downloadFile(buffer.arrayBuffer, filename)
            saved(FileHolder.Qst(fileHandle))
        }
    }

    private suspend fun saveAsBinDat(quest: QuestModel, saved: (FileHolder?) -> Unit) {
        val compress = compressed.value
        val headerFilename = filename.value.trim()
        // Strip .qst extension if present to derive a base name.
        val baseName = headerFilename
            .removeSuffix(".qst")
            .removeSuffix(".bin")
            .removeSuffix(".dat")

        val binFilename = "$baseName.bin"
        val datFilename = "$baseName.dat"

        var (bin, dat) = writeQuestToBinDat(
            convertQuestFromModel(quest),
            version.value,
        )

        if (compress) {
            bin = prsCompress(bin.cursor()).buffer()
            dat = prsCompress(dat.cursor()).buffer()
        }

        if (UserAgentFeatures.fileSystemApi) {
            val binHandle = showSaveFilePicker(
                listOf(
                    FileType("BIN file", mapOf("application/octet-stream" to setOf(".bin")))
                )
            )

            if (binHandle != null) {
                binHandle.writableStream().use { it.write(bin.arrayBuffer).await() }

                val datHandle = showSaveFilePicker(
                    listOf(
                        FileType("DAT file", mapOf("application/octet-stream" to setOf(".dat")))
                    )
                )

                if (datHandle != null) {
                    datHandle.writableStream().use { it.write(dat.arrayBuffer).await() }

                    saved(FileHolder.BinDat(binHandle, datHandle, compress))
                }
            }
        } else {
            val binHandle = downloadFile(bin.arrayBuffer, binFilename)
            val datHandle = downloadFile(dat.arrayBuffer, datFilename)
            saved(FileHolder.BinDat(binHandle, datHandle, compress))
        }
    }

    private suspend fun saveAsLobbyDat(quest: QuestModel, saved: (FileHolder?) -> Unit) {
        val baseName = filename.value.trim().removeSuffix(".dat")
        val datFilename = "$baseName.dat"
        val objectData = extractLobbyObjectData(quest)

        val fileHandle = if (UserAgentFeatures.fileSystemApi) {
            showSaveFilePicker(
                listOf(FileType("Lobby object data", mapOf("application/octet-stream" to setOf(".dat"))))
            )
        } else {
            downloadFile(objectData.arrayBuffer, datFilename)
        }

        if (fileHandle != null) {
            if (fileHandle is FileHandle.System) fileHandle.writeBuffer(objectData)
            saved(FileHolder.LobbyDat(fileHandle, datFilename))
        }
    }

    private suspend fun saveAsFreeRoam(quest: QuestModel, saved: (FileHolder?) -> Unit) {
        if (!UserAgentFeatures.directoryPickerApi) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(
                        Severity.Error,
                        "Saving as Free Roam requires a browser that supports the File System Access API (Chrome or Edge).",
                    )
                    .failure()
            )
            return
        }

        // If the quest was already loaded from a free roam directory, save back to it.
        val holder = fileHolder.value
        if (holder is FileHolder.FreeRoamDir) {
            saveToFreeRoamDir(holder, quest, saved)
            return
        }

        setResult(
            PwResult.build<Nothing>(logger)
                .addProblem(
                    Severity.Error,
                    "Save As Free Roam is only supported for quests originally loaded from a free roam directory. Use Save (Ctrl+S) instead.",
                )
                .failure()
        )
    }

    fun dismissSaveAsDialog() {
        _saveAsDialogVisible.value = false
    }

    fun dismissResultDialog() {
        _resultDialogVisible.value = false
    }

    fun undo() {
        questEditorStore.undo()
    }

    fun redo() {
        questEditorStore.redo()
    }

    fun setCurrentArea(areaAndLabel: AreaAndLabel) {
        // Clear selected section when area changes to avoid showing wrong sections
        clearSelectedSection()
        // Store the selected AreaAndLabel to preserve variant information
        _selectedAreaAndLabel.value = areaAndLabel
        settingAreaFromDropdown = true
        try {
            // Publish one consistent area selection so renderers never observe a new logical floor
            // with the previous area's variant while asynchronous meshes are being loaded.
            mutate {
                questEditorStore.setCurrentFloorIds(areaAndLabel.floorIds)
                questEditorStore.setCurrentArea(areaAndLabel.area)
                questEditorStore.setCurrentAreaVariant(areaAndLabel.variant)
            }
        } finally {
            settingAreaFromDropdown = false
        }
    }

    fun setShowCollisionGeometry(show: Boolean) {
        questEditorUiStore.setShowCollisionGeometry(show)
    }

    fun setUltimate(ultimate: Boolean) {
        questEditorUiStore.setUltimate(ultimate)
    }

    fun setShowSectionIds(show: Boolean) {
        questEditorUiStore.setShowSectionIds(show)
    }

    fun setShowDoorIds(show: Boolean) {
        questEditorUiStore.setShowDoorIds(show)
    }

    fun setShowEntityDirections(show: Boolean) {
        questEditorUiStore.setShowEntityDirections(show)
    }

    fun setSpawnMonstersOnGround(spawn: Boolean) {
        questEditorUiStore.setSpawnMonstersOnGround(spawn)
    }

    fun setShowOriginPoint(show: Boolean) {
        questEditorUiStore.setShowOriginPoint(show)
    }

    fun setShowQuestParticles(show: Boolean) {
        questEditorUiStore.setShowQuestParticles(show)
    }

    fun setWalkthroughPlayer(player: WalkthroughPlayer) {
        questEditorUiStore.setWalkthroughPlayer(player)
    }

    fun setShowFogBoundaries(show: Boolean) {
        questEditorUiStore.setShowFogBoundaries(show)
    }

    fun setSelectedSection(section: SectionModel?) {
        // Update the quest editor store (which is what selectedSection references)
        questEditorStore.setSelectedSection(section)
    }

    fun goToSelectedSection() {
        selectedSection.value?.let { section ->
            questEditorStore.goToSection(section.id)
        }
    }

    fun filterSections(text: String) {
        val trimmed = text.trim()
        gotoSectionFilter.value =
            if (trimmed.isEmpty()) {
                { true }
            } else {
                { it.id.toString().contains(trimmed) }
            }
    }

    fun selectSection(section: SectionModel) {
        ensureSectionsLoaded()
        setSelectedSection(section)
        questEditorStore.goToSection(section.id)
    }

    fun setGotoWorldX(x: Double) {
        _gotoWorldX.value = x
        commitGotoWorldPosition()
    }

    fun setGotoWorldY(y: Double) {
        _gotoWorldY.value = y
        commitGotoWorldPosition()
    }

    fun setGotoWorldZ(z: Double) {
        _gotoWorldZ.value = z
        commitGotoWorldPosition()
    }

    private fun commitGotoWorldPosition() {
        questEditorStore.goToWorldPosition(
            _gotoWorldX.value,
            _gotoWorldY.value,
            _gotoWorldZ.value,
        )
    }

    /**
     * Paste interceptor for the goto X/Y/Z inputs. When the clipboard contains 2+ numbers,
     * distribute them to X/Y/Z (missing trailing values default to 0), commit a goto, and
     * suppress the browser's default single-value paste. When it's a single number we let the
     * default paste handle it normally.
     *
     * Returns true when the paste was consumed.
     */
    fun smartPasteGotoPosition(text: String): Boolean {
        val parts = NUMBER_REGEX.findAll(text).map { it.value }.take(3).toList()
        if (parts.size < 2) return false
        val x = parts.getOrNull(0)?.toDoubleOrNull() ?: 0.0
        val y = parts.getOrNull(1)?.toDoubleOrNull() ?: 0.0
        val z = parts.getOrNull(2)?.toDoubleOrNull() ?: 0.0
        _gotoWorldX.value = x
        _gotoWorldY.value = y
        _gotoWorldZ.value = z
        questEditorStore.goToWorldPosition(x, y, z)
        return true
    }

    /**
     * Trigger section loading when user interacts with the section dropdown
     */
    fun ensureSectionsLoaded() {
        val quest = questEditorStore.currentQuest.value
        val areaVariant = questEditorStore.currentAreaVariant.value

        if (areaVariant != null) {
            // Use quest episode if available, otherwise default to Episode I
            val episode = quest?.episode ?: Episode.I
            // Check if sections are already loaded
            val loadedSections = areaStore.getLoadedSections(episode, areaVariant)
            if (loadedSections == null) {
                // We'll trigger this from the store instead where scope is available
                questEditorStore.requestSectionLoading(episode, areaVariant)
            }
        }
    }

    /**
     * Clear selected section when area changes
     */
    fun clearSelectedSection() {
        // Clear the quest editor store selection (which selectedSection references)
        questEditorStore.setSelectedSection(null)
    }

    // Free roam variant change methods (delegated to freeRoam controller)

    suspend fun setFreeRoamV1(v1: Int) {
        freeRoam.setFreeRoamV1(v1, ::handleFreeRoamReload)
    }

    suspend fun setFreeRoamV2(v2: Int) {
        freeRoam.setFreeRoamV2(v2, ::handleFreeRoamReload)
    }

    suspend fun setFreeRoamOffline(offline: Boolean) {
        freeRoam.setFreeRoamOffline(offline, ::handleFreeRoamReload)
    }

    suspend fun setFreeRoamUltimate(ultimate: Boolean) {
        freeRoam.setFreeRoamUltimate(ultimate, ::handleFreeRoamReload)
    }

    private suspend fun handleFreeRoamReload(params: FreeRoamReloadParams) {
        // Preserve current area ID and variant ID across variant reload.
        val previousAreaId = currentArea.value?.area?.id
        val previousVariantId = currentArea.value?.variant?.id

        try {
            val result = questEditorStore.getFreeRoamQuest(
                params.gameDirHandle, params.info, params.v1, params.v2,
            )
            setCurrentQuest(
                FileHolder.FreeRoamDir(params.gameDirHandle, result.binName, result.datFilesByFloor),
                Version.BB_V4,
                result.questModel,
            )

            // Restore area selection by setting the store's area/variant.
            // Don't set _selectedAreaAndLabel — let currentArea's fallback logic
            // find the matching AreaAndLabel from the current areas list, so it
            // stays in sync when areas is recomputed.
            if (previousAreaId != null) {
                // Match by both area ID and variant ID to correctly restore multi-variant areas
                // (e.g., challenge quests where the same area ID has multiple variants).
                val match = areas.value.find {
                    it.area.id == previousAreaId &&
                        (previousVariantId == null || it.variant?.id == previousVariantId)
                } ?: areas.value.find { it.area.id == previousAreaId }
                if (match != null) {
                    questEditorStore.setCurrentArea(match.area)
                    questEditorStore.setCurrentAreaVariant(match.variant)
                }
            }
        } catch (e: Throwable) {
            setResult(
                PwResult.build<Nothing>(logger)
                    .addProblem(Severity.Error, "Couldn't reload free roam area.", cause = e)
                    .failure()
            )
        }
    }

    private fun setFileHolder(fileHolder: FileHolder?) {
        _availableSaveFormats.replaceAll(
            if (fileHolder is FileHolder.LobbyDat) listOf(SaveFormat.LOBBY_DAT)
            else standardSaveFormats
        )
        // Default save format based on source format.
        setSaveFormat(when (fileHolder) {
            is FileHolder.Qst -> SaveFormat.QST
            is FileHolder.BinDat -> SaveFormat.BIN_DAT
            is FileHolder.LobbyDat -> SaveFormat.LOBBY_DAT
            is FileHolder.FreeRoamDir -> SaveFormat.FREE_ROAM
            null -> SaveFormat.QST
        })

        // Default compression based on source format.
        setCompressed(when (fileHolder) {
            is FileHolder.Qst -> true
            is FileHolder.BinDat -> fileHolder.compressed
            is FileHolder.LobbyDat -> false
            is FileHolder.FreeRoamDir -> false
            null -> true
        })

        setFilename(
            when (fileHolder) {
                is FileHolder.Qst -> fileHolder.file.basename() ?: fileHolder.file.name

                is FileHolder.BinDat ->
                    fileHolder.binFile.basename()
                        ?: fileHolder.datFile.basename()
                        ?: fileHolder.binFile.name

                is FileHolder.LobbyDat ->
                    (fileHolder.file?.basename() ?: fileHolder.defaultName).removeSuffix(".dat")

                is FileHolder.FreeRoamDir ->
                    fileHolder.binName
                        ?: fileHolder.datFilesByFloor.values.firstOrNull()?.first
                        ?: "Free Roam"

                null -> ""
            }
        )
        this.fileHolder.value = fileHolder
    }

    private suspend fun setCurrentQuest(
        fileHolder: FileHolder?,
        version: Version,
        quest: QuestModel,
    ) {
        saveCoverage = when (fileHolder) {
            is FileHolder.LobbyDat -> SaveFormat.LOBBY_DAT to QuestSaveCoverage.forLobby(quest)
            is FileHolder.FreeRoamDir -> SaveFormat.FREE_ROAM to QuestSaveCoverage.forFreeRoam(
                quest, fileHolder.binName, fileHolder.datFilesByFloor,
            )
            else -> null
        }
        setFileHolder(fileHolder)
        setVersion(version)
        // Reset area selection when loading a new quest
        _selectedAreaAndLabel.value = null
        // Note: No need to clear selectedSection here since setCurrentQuest() in the store already does this
        questEditorStore.setCurrentQuest(quest)

        // Don't override the area/variant - let QuestEditorStore handle the correct initialization
        // The store already correctly sets the area based on floor 0 mapping or appropriate fallback
    }

    private suspend fun setCurrentQuest(
        fileHolder: FileHolder?,
        version: Version,
        quest: Quest,
    ) {
        setCurrentQuest(
            fileHolder,
            version,
            convertQuestToModel(quest, areaStore::getVariant, questEditorStore.npcPlacementPolicy),
        )
    }

    private fun setResult(result: PwResult<*>) {
        _result.value = result

        if (result.problems.isNotEmpty()) {
            _resultDialogVisible.value = true
        }
    }

    private suspend fun saveToFreeRoamDir(
        holder: FileHolder.FreeRoamDir, quest: QuestModel, saved: (FileHolder?) -> Unit,
    ) {
        val convertedQuest = convertQuestFromModel(quest)
        val entityDataByFloor = extractRawEntityDataByFloor(convertedQuest)
        val files = buildList {
            holder.binName?.let { name ->
                add(name to writeQuestToBinDat(convertedQuest, version.value).first)
            }
            for ((floorId, names) in holder.datFilesByFloor) {
                val (objName, npcName, evtName) = names
                val (objects, npcs) = entityDataByFloor[floorId]
                    ?: (Buffer.withSize(0, Endianness.Little) to Buffer.withSize(0, Endianness.Little))
                // Empty outputs clear removed entities; an absent target is not a file name.
                if (objName.isNotEmpty()) add(objName to objects)
                if (npcName.isNotEmpty()) add(npcName to npcs)
                if (evtName.isNotEmpty()) {
                    add(evtName to (writeEventDataForFloor(convertedQuest.events, floorId)
                        ?: Buffer.withSize(0, Endianness.Little)))
                }
            }
        }
        val dataDir = try {
            holder.dirHandle.getDirectoryHandle("data").await()
        } catch (_: Throwable) {
            holder.dirHandle
        }

        // 1. Backup original files.
        val backupDir = holder.dirHandle.getDirectoryHandle("backup", obj { create = true }).await()

        val allDatNames = holder.datFilesByFloor.values.flatMap { (obj, npc, evt) -> listOf(obj, npc, evt) }
        val allNames = (listOfNotNull(holder.binName) + allDatNames).filter { it.isNotEmpty() }
        for (name in allNames) {
            try {
                val originalData = dataDir.getFileHandle(name).await()
                    .getFile().await().arrayBuffer().await()

                val backupHandle = backupDir.getFileHandle(name, obj { create = true }).await()
                val writable = backupHandle.createWritable().await()
                writable.write(originalData).await()
                writable.close().await()
            } catch (e: Throwable) {
                // File might not exist (e.g., empty floor) — that's expected.
                // Log unexpected errors so the user can investigate if backup fails unexpectedly.
                if (e.message?.contains("not found", ignoreCase = true) == false &&
                    e.message?.contains("NotFoundError", ignoreCase = true) == false
                ) {
                    logger.warn(e) { "Failed to back up file $name before saving." }
                }
            }
        }

        for ((name, buffer) in files) {
            val handle = dataDir.getFileHandle(name, obj { create = true }).await()
            val writable = handle.createWritable().await()
            writable.write(buffer.arrayBuffer).await()
            writable.close().await()
        }

        saved(null)
    }



    private sealed class QuestLoadStrategy {
        class Qst(val file: FileHandle) : QuestLoadStrategy()
        class BinDat(val binFile: FileHandle, val datFile: FileHandle) : QuestLoadStrategy()
        class LobbyDat(val file: FileHandle, val variant: Int) : QuestLoadStrategy()
        class FreeRoam(val info: FreeRoamAreaInfo) : QuestLoadStrategy()
    }

    private sealed class FileHolder {
        class Qst(val file: FileHandle) : FileHolder()
        class BinDat(val binFile: FileHandle, val datFile: FileHandle, val compressed: Boolean) : FileHolder()
        class LobbyDat(val file: FileHandle?, val defaultName: String) : FileHolder()
        class FreeRoamDir(
            val dirHandle: FileSystemDirectoryHandle,
            val binName: String?,
            /** Map of floorId → (objDatName, npcDatName, evtName) for saving back. */
            val datFilesByFloor: Map<Int, Triple<String, String, String>>,
        ) : FileHolder()
    }

    companion object {
        private fun extractLobbyObjectData(quest: QuestModel): Buffer {
            val entityDataByFloor = extractRawEntityDataByFloor(convertQuestFromModel(quest))
            return entityDataByFloor[LOBBY_FLOOR_ID]?.first
                ?: Buffer.withSize(0, Endianness.Little)
        }

        /**
         * Detects Japanese quest files by filename suffix (e.g., `quest01_j.bin`).
         */
        fun isJapaneseFilename(filename: String): Boolean {
            val base = filename.substringBeforeLast('.')
            return base.endsWith("_j")
        }

        /** Matches signed integer / decimal components used by the goto-position parser. */
        private val NUMBER_REGEX = Regex("""-?\d+(?:\.\d+)?""")
    }
}
